import { useRef, useState, useEffect, useCallback } from "react";
import throttle from "lodash/throttle";
import { isEmpty } from "utils/common-util";

const useDetectScrollEnd = (config) => {
  const scrollbarRef = useRef();
  const [isEnd, setEnd] = useState(false);
  const configRef = useRef(config);
  const enabledDetectionRef = useRef(config?.enabledDetection);

  useEffect(() => {
    configRef.current = config;

    if (isEmpty(config?.enabledDetection)) {
      enabledDetectionRef.current = config?.enabledDetection ?? true;
    }
  }, [config]);

  const checkScrollEnd = useCallback((el) => {
    if (!enabledDetectionRef.current) return;

    el = el ?? scrollbarRef.current;

    if (el) {
      const offsetBottom = configRef.current?.offset ?? 10;
      const { scrollHeight, clientHeight, offsetHeight, scrollTop } = el;
      const noScroll = scrollHeight === clientHeight;
      const end = scrollTop + offsetHeight >= scrollHeight - offsetBottom;
      if (noScroll || end) {
        setEnd(true);
      } else {
        // Restart !
        // if config.once is true : no restart if end already.
        setEnd((isEnd) => {
          if (isEnd) {
            if (configRef.current?.once) {
              return true;
            }
          }

          return false;
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!isEnd) {
      checkScrollEnd();

      const intCheck = setInterval(() => {
        checkScrollEnd();
      }, configRef.current.interval ?? 1000);

      return () => clearInterval(intCheck);
    }
  }, [isEnd, checkScrollEnd]);

  const elRef = useCallback(
    (node) => {
      if (node !== null) {
        scrollbarRef.current = node;
        checkScrollEnd(node);
      }
    },
    [checkScrollEnd]
  );

  useEffect(() => {
    const scrollListener = throttle((evt) => {
      if (scrollbarRef.current?.contains(evt.target)) {
        checkScrollEnd(scrollbarRef.current);
      }
    }, configRef.current?.delay ?? 100);
    window.addEventListener("scroll", scrollListener, true);
    return () => window.removeEventListener("scroll", scrollListener, true);
  }, [scrollbarRef, checkScrollEnd]);

  const reset = useCallback(
    (checkScroll = true) => {
      setEnd(false);
      if (checkScroll) {
        checkScrollEnd();
      }
    },
    [checkScrollEnd]
  );

  const enable = useCallback(
    (enable = true) => {
      enabledDetectionRef.current = enable;
      checkScrollEnd();
    },
    [checkScrollEnd]
  );

  return [elRef, isEnd, reset, enable];
};

export default useDetectScrollEnd;
